/**
 * Stream configuration presets for Gamma video player.
 *
 * Each configuration includes:
 * - bentoUrl: Primary source for video data (preferred)
 * - fallbackVideoData: Complete VideoData structure for fallback
 */

/**
 * Helper function to create complete fallback VideoData structure
 * Accepts a single named-argument object for clarity and future extensibility.
 * @param {object} options
 * @param {string} options.id - Unique short identifier for the preset (e.g. 'dateline')
 * @param {string} [options.headline] - Primary headline text
 * @param {string} [options.description] - Primary description text
 * @param {string} [options.publisher] - Publisher name
 * @returns {object} Complete VideoData structure for fallback usage
 */
const createFallbackVideoData = ({
  id,
  headline = '',
  description = '',
  publisher = '',
}) => ({
  id: `${id}-fallback`,
  headline: { primary: headline },
  description: { primary: description },
  publisher: { name: publisher },
  primaryImage: {
    id: '',
    headline: { primary: '' },
    caption: '',
    altText: headline,
    url: { primary: '', slug: '', short: '' },
    encodingFormat: '',
    height: 0,
    width: 0,
    authors: [],
    source: { name: publisher },
    graphicContent: false,
  },
  duration: 0,
  videoAssets: [],
  mpxMetadata: {
    guid: `${id}-fallback-guid`,
    mpxId: `${id}-fallback-mpx`,
  },
  url: {
    primary: '',
    slug: '',
    short: '',
  },
  playable: true,
});

export const DEFAULT_MPX_CONFIG = {
  baseBentoUrl: 'https://www.nbcnews.com',
  ids: {
    DATELINE: '248670277649',
    NBC_SPORTS: '248672325694',
    NEWS_NOW: '248671301631',
    NOTICIAS_AHORA: '248672325771',
    SKY_NEWS: '248671301664',
    TELEMUNDO_DEPORTES_AHORA: '248672837934',
    TODAY_ALL_DAY: '248670277645',
  },
};

const LIVE_VIDEO_FALL_BACK_DATA = {
  DATELINE: {
    fallbackProps: {
      id: 'dateline',
      headline: 'Dateline',
      description: 'Dateline 247',
      publisher: 'NBC News',
    },
  },
  NBC_SPORTS: {
    fallbackProps: {
      id: 'nbc-sports',
      headline: 'NBC Sports NOW',
      description: 'Live streaming coverage from NBC Sports NOW',
      publisher: 'NBC Sports',
    },
  },
  NEWS_NOW: {
    fallbackProps: {
      id: 'news-now',
      headline: 'NBC News Now',
      description: 'Live streaming news coverage from NBC News Now',
      publisher: 'NBC News',
    },
  },
  NOTICIAS_AHORA: {
    fallbackProps: {
      id: 'noticias-ahora',
      headline: 'Noticias Telemundo AHORA',
      description: 'Las últimas noticias y las historias más importantes para la comunidad latina.',
      publisher: 'NBC News',
    },
  },
  SKY_NEWS: {
    fallbackProps: {
      id: 'sky-news',
      headline: 'Sky News',
      description: 'Sky News Live',
      publisher: 'Sky News',
    },
  },
  TELEMUNDO_DEPORTES_AHORA: {
    fallbackProps: {
      id: 'telemundo-deportes',
      headline: 'Telemundo Deportes AHORA',
      description: 'Telemundo Deportes AHORA Live',
      publisher: 'Telemundo',
    },
  },
  TODAY_ALL_DAY: {
    fallbackProps: {
      id: 'today-all-day',
      headline: 'TODAY All Day',
      description: 'TODAY All Day is a 24/7 streaming channel from NBC News where viewers can find everything they love about TODAY.',
      publisher: 'Today',
    },
  },
};

/**
 * Get numeric ID for a preset from parsed mpxIds map.
 * @param {{baseBentoUrl?:string, ids?:Record<string,string>}|null} parsedJson
 * @param {string} presetKey
 * @returns {string|null}
 */
function getMpxIdFromPresets(parsedJson, presetKey) {
  if (!parsedJson || !parsedJson.ids) return null;
  // const normalized = normalizePresetKey(presetKey);
  return parsedJson?.ids[presetKey] || null;
}

/**
 * Build a full bento URL from baseBentoUrl and numeric id.
 * numericId should be digits only (e.g. '245801029595'). Returns null if missing.
 * @param {string|null} baseBentoUrl
 * @param {string|null} numericId
 * @returns {string|null}
 */
function buildBentoUrlFromBaseAndNumericId(baseBentoUrl, numericId) {
  if (!baseBentoUrl || !numericId) return null;
  const trimmedBase = String(baseBentoUrl).replace(/\/+$/, '');
  return `${trimmedBase}/bentoapi/video/mmvo${numericId}`;
}

/**
 * Build gamma stream presets from an MPX presets object.
 *
 * The function expects a parsed MPX presets object with the shape:
 * { baseBentoUrl?: string, ids?: Record<string, string> }.
 * For each known preset key this will compute a bentoUrl when a numeric MPX id
 * is available (it accepts raw digits or values like 'mmvo2457...'). If the
 * base URL or id are missing the resulting preset will contain `bentoUrl: null`
 * and a complete `fallbackVideoData` will still be provided.
 *
 * @param {{baseBentoUrl?: string, ids?: Record<string,string>}|null} mpxPreset Parsed MPX presets.
 * @returns {Record<string, {bentoUrl: string|null, fallbackVideoData: object}>} Map of presets keyed by stream name.
 */
export function buildGammaStreamConfigPresetsFromEnvJson(mpxPreset) {
  const baseBentoUrl = mpxPreset?.baseBentoUrl || null;

  const entries = Object.entries(LIVE_VIDEO_FALL_BACK_DATA)
    .map(([presetKey, { fallbackProps }]) => {
      const mpxId = getMpxIdFromPresets(mpxPreset, presetKey);
      const bentoUrl = buildBentoUrlFromBaseAndNumericId(baseBentoUrl, mpxId);

      return [
        presetKey,
        {
          bentoUrl,
          fallbackVideoData: createFallbackVideoData(fallbackProps),
        },
      ];
    });

  return Object.fromEntries(entries);
}
